import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, Edit, ChevronLeft, GripVertical, Video, Image, FileText, QrCode, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { getSessionToken } from "@/utils/auth";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const CONTENT_TYPES = [
  { value: "video", label: "Video", icon: Video },
  { value: "image", label: "Image", icon: Image },
  { value: "pdf", label: "PDF", icon: FileText },
  { value: "text", label: "Text", icon: FileText },
  { value: "qr", label: "QR Code", icon: QrCode },
];

const VISIBILITY_OPTIONS = [
  { value: "all", label: "Everyone" },
  { value: "tech", label: "Techs Only" },
  { value: "admin", label: "Admins Only" },
];

const DEFAULT_CATEGORIES = [
  { name: "Training", description: "How-to videos and best practices", icon: "training" },
  { name: "Pricing & Services", description: "Current pricing and upsells", icon: "pricing" },
  { name: "Payments", description: "Accepted methods and QR codes", icon: "payments" },
  { name: "Policies & Expectations", description: "Quality standards and customer handling", icon: "policies" },
  { name: "Quick Reference", description: "Measurements, screen types, tools", icon: "reference" },
];

export default function ManageResources() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Dialogs
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Form state
  const [editingResource, setEditingResource] = useState(null);
  const [resourceForm, setResourceForm] = useState({
    title: "",
    description: "",
    content_type: "text",
    content_url: "",
    content_text: "",
    category_ids: [],
    visibility: "all",
    order: 0
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    icon: "",
    order: 0
  });

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      fetchCategories();
      fetchResources();
    }
  }, [user, selectedCategory]);

  const fetchUser = async () => {
    try {
      const token = getSessionToken();
      if (!token) {
        navigate("/login");
        return;
      }
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.role !== "Admin") {
        toast.error("Admin access required");
        navigate("/resources");
        return;
      }
      setUser(response.data);
    } catch (error) {
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/resource-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
      
      // Auto-create default categories if none exist
      if (response.data.length === 0) {
        for (let i = 0; i < DEFAULT_CATEGORIES.length; i++) {
          await axios.post(`${API}/resource-categories`, {
            ...DEFAULT_CATEGORIES[i],
            order: i
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        fetchCategories();
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchResources = async () => {
    try {
      const token = getSessionToken();
      const params = selectedCategory ? { category_id: selectedCategory } : {};
      const response = await axios.get(`${API}/resources`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setResources(response.data);
    } catch (error) {
      console.error("Failed to fetch resources:", error);
    }
  };

  const handleSaveResource = async () => {
    try {
      const token = getSessionToken();
      
      if (editingResource) {
        await axios.put(`${API}/resources/${editingResource.resource_id}`, resourceForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Resource updated");
      } else {
        await axios.post(`${API}/resources`, resourceForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Resource created");
      }
      
      setResourceDialogOpen(false);
      setEditingResource(null);
      resetResourceForm();
      fetchResources();
    } catch (error) {
      toast.error("Failed to save resource");
    }
  };

  const handleDeleteResource = async () => {
    try {
      const token = getSessionToken();
      await axios.delete(`${API}/resources/${itemToDelete.resource_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Resource deleted");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchResources();
    } catch (error) {
      toast.error("Failed to delete resource");
    }
  };

  const openEditResource = (resource) => {
    setEditingResource(resource);
    setResourceForm({
      title: resource.title,
      description: resource.description || "",
      content_type: resource.content_type,
      content_url: resource.content_url || "",
      content_text: resource.content_text || "",
      category_ids: resource.category_ids || [],
      visibility: resource.visibility || "all",
      order: resource.order || 0
    });
    setResourceDialogOpen(true);
  };

  const resetResourceForm = () => {
    setResourceForm({
      title: "",
      description: "",
      content_type: "text",
      content_url: "",
      content_text: "",
      category_ids: [],
      visibility: "all",
      order: 0
    });
  };

  const toggleCategory = (categoryId) => {
    setResourceForm(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <Header user={user} />
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-zinc-600 mb-4 hover:text-zinc-900"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Settings
        </button>
        
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-barlow text-2xl font-bold text-zinc-900 uppercase">
            Manage Resources
          </h1>
          <button
            onClick={() => {
              resetResourceForm();
              setEditingResource(null);
              setResourceDialogOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#207024] text-white rounded-lg font-semibold"
            data-testid="add-resource-btn"
          >
            <Plus className="w-5 h-5" />
            Add Resource
          </button>
        </div>
        
        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-4 -mx-4 px-4">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              !selectedCategory
                ? "bg-[#207024] text-white"
                : "bg-white border-2 border-zinc-200 text-zinc-600"
            }`}
          >
            All ({resources.length})
          </button>
          {categories.map((cat) => (
            <button
              key={cat.category_id}
              onClick={() => setSelectedCategory(cat.category_id)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                selectedCategory === cat.category_id
                  ? "bg-[#207024] text-white"
                  : "bg-white border-2 border-zinc-200 text-zinc-600"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
        
        {/* Resources List */}
        <div className="space-y-3">
          {resources.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 bg-white rounded-xl border">
              No resources yet. Click "Add Resource" to create one.
            </div>
          ) : (
            resources.map((resource) => {
              const TypeIcon = CONTENT_TYPES.find(t => t.value === resource.content_type)?.icon || FileText;
              return (
                <div
                  key={resource.resource_id}
                  className="bg-white rounded-xl border p-4 flex items-center gap-3"
                >
                  <GripVertical className="w-5 h-5 text-zinc-300 cursor-grab" />
                  
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                    <TypeIcon className="w-5 h-5 text-[#207024]" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-zinc-900 truncate">{resource.title}</h3>
                      {resource.visibility !== "all" && (
                        <span className="text-xs px-2 py-0.5 bg-zinc-100 rounded text-zinc-600">
                          {resource.visibility === "admin" ? "Admin" : "Tech"}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500 truncate">
                      {resource.content_type} • {resource.category_ids.length} categories
                    </p>
                  </div>
                  
                  <button
                    onClick={() => openEditResource(resource)}
                    className="p-2 text-zinc-400 hover:text-zinc-600"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  
                  <button
                    onClick={() => {
                      setItemToDelete(resource);
                      setDeleteDialogOpen(true);
                    }}
                    className="p-2 text-zinc-400 hover:text-red-600"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
      
      {/* Resource Dialog */}
      <Dialog open={resourceDialogOpen} onOpenChange={setResourceDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingResource ? "Edit Resource" : "Add Resource"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={resourceForm.title}
                onChange={(e) => setResourceForm({...resourceForm, title: e.target.value})}
                placeholder="Resource title"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={resourceForm.description}
                onChange={(e) => setResourceForm({...resourceForm, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            
            <div>
              <Label>Content Type *</Label>
              <Select
                value={resourceForm.content_type}
                onValueChange={(value) => setResourceForm({...resourceForm, content_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {["video", "image", "pdf", "qr"].includes(resourceForm.content_type) && (
              <div>
                <Label>
                  {resourceForm.content_type === "qr" ? "QR Code Image URL" : "Content URL"} *
                </Label>
                <Input
                  value={resourceForm.content_url}
                  onChange={(e) => setResourceForm({...resourceForm, content_url: e.target.value})}
                  placeholder={
                    resourceForm.content_type === "video" ? "YouTube or Vimeo URL" :
                    resourceForm.content_type === "qr" ? "QR code image URL" :
                    "URL to file"
                  }
                />
              </div>
            )}
            
            {resourceForm.content_type === "qr" && (
              <div>
                <Label>Payment Link (optional)</Label>
                <Input
                  value={resourceForm.content_text}
                  onChange={(e) => setResourceForm({...resourceForm, content_text: e.target.value})}
                  placeholder="https://venmo.com/..."
                />
              </div>
            )}
            
            {resourceForm.content_type === "text" && (
              <div>
                <Label>Content *</Label>
                <Textarea
                  value={resourceForm.content_text}
                  onChange={(e) => setResourceForm({...resourceForm, content_text: e.target.value})}
                  placeholder="Enter text content..."
                  rows={6}
                />
              </div>
            )}
            
            <div>
              <Label>Categories</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {categories.map((cat) => (
                  <button
                    key={cat.category_id}
                    type="button"
                    onClick={() => toggleCategory(cat.category_id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      resourceForm.category_ids.includes(cat.category_id)
                        ? "bg-[#207024] text-white"
                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Visibility</Label>
              <Select
                value={resourceForm.visibility}
                onValueChange={(value) => setResourceForm({...resourceForm, visibility: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VISIBILITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Order (lower = first)</Label>
              <Input
                type="number"
                value={resourceForm.order}
                onChange={(e) => setResourceForm({...resourceForm, order: parseInt(e.target.value) || 0})}
              />
            </div>
          </div>
          
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={() => setResourceDialogOpen(false)}
              className="flex-1 py-2.5 border-2 border-zinc-200 rounded-lg font-semibold text-zinc-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveResource}
              disabled={!resourceForm.title || (!resourceForm.content_url && !resourceForm.content_text)}
              className="flex-1 py-2.5 bg-[#207024] text-white rounded-lg font-semibold disabled:opacity-50"
            >
              {editingResource ? "Update" : "Create"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Resource?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{itemToDelete?.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteResource}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <BottomNav />
    </div>
  );
}
